/** @jsxImportSource @emotion/react */
import { useEffect, useMemo, useRef, useState } from "react";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

const getRemainingMs = (expiresAt) => {
  const expiry = new Date(expiresAt).getTime();
  if (!expiresAt || Number.isNaN(expiry)) return 0;
  return Math.max(0, expiry - Date.now());
};

const formatRemaining = (ms) => {
  const totalSeconds = Math.ceil(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

const styles = {
  timer: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    borderRadius: 999,
    padding: "8px 12px",
    background: "#fff7ed",
    color: "#9a3412",
    fontWeight: 700,
    fontSize: 13,
    whiteSpace: "nowrap",
  },
  expired: {
    background: "#fef2f2",
    color: "#b91c1c",
  },
  icon: {
    fontSize: 18,
  },
};

const ReservationCountdown = ({ expiresAt, onExpire }) => {
  const [remainingMs, setRemainingMs] = useState(() => getRemainingMs(expiresAt));
  const hasExpiredRef = useRef(false);
  const isExpired = remainingMs <= 0;

  useEffect(() => {
    const initialRemainingMs = getRemainingMs(expiresAt);
    hasExpiredRef.current = initialRemainingMs <= 0;
    setRemainingMs(initialRemainingMs);

    const timer = setInterval(() => {
      const nextRemainingMs = getRemainingMs(expiresAt);
      setRemainingMs(nextRemainingMs);
      if (nextRemainingMs <= 0 && !hasExpiredRef.current) {
        hasExpiredRef.current = true;
        if (onExpire) onExpire();
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [expiresAt, onExpire]);

  const label = useMemo(
    () =>
      isExpired
        ? "Payment window closed"
        : `Pay within ${formatRemaining(remainingMs)}`,
    [isExpired, remainingMs],
  );

  return (
    <span css={[styles.timer, isExpired && styles.expired]}>
      <AccessTimeIcon css={styles.icon} />
      {label}
    </span>
  );
};

export default ReservationCountdown;
